import { Alert, AlertTitle } from '@mui/material';
import { logger } from '@sentry/react';
import { cn, type ClassValue } from 'cn';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type Resources from '~/types/i18n-resources';

import { VestigasHTTPError } from '@vestigas/data';

import { toCamelCase } from '@vestigas/utils';

const validationErrorDetailSchema = z.object({
  loc: z.array(z.union([z.string(), z.number()])),
  msg: z.string(),
  type: z.string(),
});

const messageErrorDetailSchema = z.object({
  error: z.string().optional(),
  message: z.string(),
});

type DeepGet<
  T,
  Path extends string,
> = Path extends `${infer Head}.${infer Tail}`
  ? Head extends keyof T
    ? DeepGet<T[Head], Tail>
    : never
  : Path extends keyof T
    ? T[Path]
    : never;

type P<Ns extends keyof Resources, Path extends string> = {
  className?: ClassValue;
  error: VestigasHTTPError | null;
  isSubmitting: boolean;
  fieldTransKey?: (field: keyof DeepGet<Resources[Ns], Path>) => string;
};

export type ResolveFieldLabelFromLocProps<
  Ns extends keyof Resources,
  Path extends string,
> = Omit<P<Ns, Path>, 'isSubmitting'> & {
  readonly loc: ReadonlyArray<string | number>;
};

/**
 * For each error item, `fieldTransKey` is tried on the last `loc` segment first (camelCased).
 * If that yields no label (falsy or throws), the previous segment is tried, until all segments
 * are exhausted; then the fallback derived from `loc` (last segment, or joined path) is shown.
 * If no translation is found, the field is logged as a warning. `defaultValue: 'untranslated'` is used to identify untranslated fields.
 */
export function resolveFieldLabelFromLoc<
  Ns extends keyof Resources,
  Path extends string,
>({
  error,
  fieldTransKey,
  loc,
}: ResolveFieldLabelFromLocProps<Ns, Path>): string {
  const fieldKey = loc.at(-1);
  const fallbackFieldName =
    fieldKey !== undefined ? String(fieldKey) : loc.join('.');

  if (!fieldTransKey || loc.length === 0) {
    return fallbackFieldName;
  }

  for (let index = loc.length - 1; index >= 0; index -= 1) {
    const segment = loc[index];
    if (segment === undefined) {
      continue;
    }

    try {
      const camelCaseKey = toCamelCase(String(segment)) as keyof DeepGet<
        Resources[Ns],
        Path
      >;
      const translated = fieldTransKey(camelCaseKey);

      if (translated !== 'untranslated') {
        return translated;
      }
    } catch {
      // Try the next segment up the path.
    }
  }

  logger.warn(
    `HttpValidationErrorAlert: no field label matched for ${fallbackFieldName}`,
    {
      ...error,
    },
  );

  return fallbackFieldName;
}

/**
 * Returns the first `VestigasHTTPError` in the argument list, or `null` if none.
 * Use with mutation `error` / `failureReason` values so a single call can pick
 * the HTTP error from whichever property the framework populated (e.g. React Query
 * `error` vs `failureReason`).
 */
export function pickHttpValidationError(
  ...candidates: Array<unknown>
): VestigasHTTPError | null {
  for (const candidate of candidates) {
    if (candidate instanceof VestigasHTTPError) {
      return candidate;
    }
  }

  return null;
}

/**
 * Displays a 422 validation error from a failed mutation as an MUI Alert.
 *
 * @template Ns - i18n namespace key (e.g. `'masterData'`). Inferred from `fieldTransKey`.
 * @template Path - Dot-separated path into `Resources[Ns]` pointing to the fields record
 *   (e.g. `'ignoredArticles.form.fields'`). Inferred from `fieldTransKey`.
 *
 * @example
 * const fieldLabels: Record<string, string> = {
 *   name: t(($) => $.ignoredArticles.form.fields.name.label),
 *   reason: t(($) => $.ignoredArticles.form.fields.reason.label),
 * };
 *
 * <HttpValidationErrorAlert<'masterData', 'ignoredArticles.form.fields'>
 *   error={mutationError}
 *   isSubmitting={isPending}
 *   fieldTransKey={(field) => fieldLabels[field], {defaultValue: 'untranslated'}}
 * />
 */
export function HttpValidationErrorAlert<
  Ns extends keyof Resources,
  Path extends string,
>({ className, error, fieldTransKey, isSubmitting }: P<Ns, Path>) {
  const { t } = useTranslation('common');

  if (!error || isSubmitting) {
    return null;
  }

  const { parsedBody } = error;

  if (!parsedBody) {
    return null;
  }

  const isStructuredBody = typeof parsedBody !== 'string';
  const details = isStructuredBody ? [parsedBody.detail].flat() : [];

  return (
    <Alert severity="error" className={cn(className)}>
      {isStructuredBody ? (
        <>
          <AlertTitle>{t(($) => $.error.generic.title)}</AlertTitle>
          <ul className="space-y-1">
            {details.map((item, index) => {
              const validationDetail =
                validationErrorDetailSchema.safeParse(item);

              if (validationDetail.success) {
                if (validationDetail.data.type === 'model_attributes_type') {
                  return null;
                }

                const resolvedFieldName = resolveFieldLabelFromLoc<Ns, Path>({
                  error,
                  fieldTransKey,
                  loc: validationDetail.data.loc,
                });

                return (
                  <li key={`${validationDetail.data.loc.join('.')}-${index}`}>
                    {validationDetail.data.msg}
                    {': '}
                    <span className="font-medium">{resolvedFieldName}</span>
                  </li>
                );
              }

              const messageDetail = messageErrorDetailSchema.safeParse(item);

              return messageDetail.success ? (
                <li key={messageDetail.data.error ?? index}>
                  {messageDetail.data.message}
                </li>
              ) : null;
            })}
          </ul>
        </>
      ) : (
        parsedBody
      )}
    </Alert>
  );
}
